import { useContext } from "react";
import { Table, OverlayTrigger, Tooltip } from "react-bootstrap"
import ProgressBar from 'react-bootstrap/ProgressBar';
import DatabaseContext from "./DatabaseContext";

import { filterTable, createRowObj, findTableRow } from "../services/tableUtils";
import { createBlobDownload, dateYYYYMMMDD } from "../services/commonParse";
import { projectTotal } from "../services/baseplateService";

const SortMetalForTable = (bpList, kaptonList, tapeList, shipList) => {
  // Creating the initial items
  const table = {}
  const geometryList = [
    // Full plates
    "FLW", "FHW", "F0W", "FLT", "FHT", "F0T",
    // CuW Unique,
    "BHW", "RHW", "LHW", "SHW",
    // Ti Unique
    "THT",
    // Common
    "TLW", "TLT", "BLW", "BLT", "LLW", "LLT", "RLW", "SLW", "RLT", "5LT", "5LW"
  ]
  for (const bptype of geometryList) {
    table[bptype] = {
      // Metal counting
      "bare_bad": [], "bare_unknown": [], "bare_good": [],
      "delam": [], "recov": [],
      // Nominal in progress items
      "lam_comp": [], "lam_in": [], "lam_remote": [],
      // Completed items
      "comp_remote": [], "comp": [], "pack": [], "ship": []
    }
  }
  if (!('id' in bpList)) return table

  const reType = (bp) => {
    if (bp.tape_id) {
      const tape = findTableRow(tapeList, (t) => t.id === bp.tape_id);
      if (bp.type === "F0W" || bp.type === "F0T") {
        return bp.type[0] + tape.type[1] + bp.type[2];
      } else if (bp.type === "SLW" || bp.type === "SHW") {
        return tape.type[0] + bp.type[1] + bp.type[2];
      }
    }
    if (bp.kapton_id) {
      const kap = findTableRow(kaptonList, (k) => k.id == bp.kapton_id);
      if (bp.type === "SLW" || bp.type === "SHW") {
        return kap.type[0] + bp.type[1] + bp.type[2];
      }
    }
    return bp.type;
  }


  // Running the logic for plate sorting
  for (let idx = 0; idx < bpList.id.length; ++idx) { // Working in inverse process order
    const bp = createRowObj(bpList, idx);
    if (geometryList.find((x) => x === bp.type)) {
      if (bp.shipment_id !== null) {
        const ship = findTableRow(shipList, (ship) => ship.id === bp.shipment_id);
        if (ship.stt_idx) {
          table[bp.type]["ship"].push(bp);
        } else {
          table[bp.type]["pack"].push(bp)
        }
      } else if (bp.tape_id) {
        if (bp.remotetask_id) {
          table[reType(bp)]["comp_remote"].push(bp);
        } else {
          table[bp.type]["comp"].push(bp);
        }
      } else if (bp.kapton_id !== null) {
        if (!bp.kapton_align_passed || bp.flatness_grade === "red") {
          table[bp.type]["delam"].push(bp);
        } else if ((bp.defect_lam_delam_pad > 0) || (bp.defect_lam_glue_pad > 0) || (bp.defect_lam_glue_hole > 0)) {
          table[bp.type]["recov"].push(bp);
        } else if (bp.remotetask_id) {
          table[reType(bp)]["lam_remote"].push(bp);
        } else if (bp.height_lam0 === null) {
          table[bp.type]["lam_in"].push(bp)
        } else {
          table[bp.type]["lam_comp"].push(bp);
        }
      } else {
        if (bp.flatness_grade === "red" || bp.tolerance_grade === "red") {
          table[bp.type]["bare_bad"].push(bp);
        } else if (bp.flatness_grade === null | bp.tolerance_grade === null) {
          table[bp.type]["bare_unknown"].push(bp);
        } else {
          table[bp.type]["bare_good"].push(bp);
        }
      }
    }
  }
  return table
}

const GetVersionedCount = (kapType, kaptonList) => {
  if (!('id' in kaptonList)) return 0;
  const kapList = filterTable(kaptonList, (kap) => (
    kap.type === kapType
  ));
  const lastVer = Math.max.apply(null, kapList.version);
  let sum = 0;
  for (let idx = 0; idx < kapList.id.length; ++idx) {
    if (Number(kapList.version[idx]) === Number(lastVer)) {
      sum += Number(kapList.available[idx]);
    }
  }
  return sum;
}

export const StockTable = ({ bpSorted }) => {
  return <Table striped bordered>
    <thead>
      <tr style={{ textAlign: "center" }}>
        <th rowSpan={2}>Geometry</th>
        <th colSpan={3}>Bare item stock</th>
        <th rowSpan={2}>
          Defect<br />
          <span style={{ color: "red", marginRight: "0.5em", fontSize: "60%" }}>Unrec.</span> |
          <span style={{ color: "orange", marginLeft: "0.5em", fontSize: "60%" }}>Rec.</span>
        </th>
        <th rowSpan={2}>In. pipeline <br />
          <span style={{ color: "blue", marginRight: "0.5em", fontSize: "60%" }}>Unmeas.</span> |
          <span style={{ color: "darkgreen", marginLeft: "0.5em", fontSize: "60%" }}>Comp. Lam.</span> |
          <span style={{ color: "purple", marginLeft: "0.5em", fontSize: "60%" }}>Remote</span>
        </th>
        <th rowSpan={2}>
          Ready <br />
          <span style={{ color: "blue", marginRight: "0.5em", fontSize: "60%" }} >Single</span> |
          <span style={{ color: "darkgreen", marginLeft: "0.5em", fontSize: "60%" }}>Packed</span> |
          <span style={{ color: "purple", marginLeft: "0.5em", fontSize: "60%" }}>Remote</span>
        </th>
        <th rowSpan={2}>
          Project Overview <br />
          <span style={{ marginRight: "1em" }}>Total (w/ 5% spare)</span>  |
          <span style={{ color: "darkgreen", marginLeft: "0.3em", marginRight: "0.3em" }}>shipped</span> |
          <span style={{ color: "blue", marginLeft: "0.3em", marginRight: "0.3em" }} > ready </span> |
          <span style={{ color: "darkgray", marginLeft: "0.3em", marginRight: "0.3em" }} > Inbound </span> |
          <span style={{ color: "red", marginLeft: "0.3em", marginRight: "0.3em" }}>  missing </span> <br />
          <ProgressBar>
            <ProgressBar variant="success" now={10} key={1} label="Shipped" />
            <ProgressBar variant="success" striped now={20} key={3} label="Ready (remote)" />
            <ProgressBar variant="info" now={10} key={2} label="Ready" />
            <ProgressBar variant="warning" striped now={20} key={5} label="Partial (remote)" />
            <ProgressBar variant="warning" now={10} key={4} label="Have parts" />
            <ProgressBar style={{ backgroundColor: "gray" }} striped now={20} key={6} label="Inbound" />
          </ProgressBar>
        </th>
      </tr>
      <tr style={{ textAlign: "center" }}>
        {/*Item stock*/}
        <th>
          Metal <br />
          <span style={{ color: "black", marginRight: "0.5em", fontSize: "60%" }}>Good</span> |
          <span style={{ color: "gray", marginLeft: "0.5em", marginRight: "0.5em", fontSize: "60%" }}>Unmeas. (+in.)</span> |
          <span style={{ color: "red", marginLeft: "0.5em", fontSize: "60%" }}>Bad</span>
        </th>
        <th>
          Kapton <br />
          <span style={{ color: "black", fontSize: "60%" }}>Current</span>
          <span style={{ color: "gray", fontSize: "60%" }}>(+inbound)</span>
        </th>
        <th>
          Tape <br />
          <span style={{ color: "black", fontSize: "60%" }}>Current</span>
          <span style={{ color: "gray", fontSize: "60%" }}>(+inbound)</span>
        </th>
      </tr >
    </thead>
    <tbody>
      {/*Full plates*/}
      <SingleRow title="CuW LD Full" type="FLW" mrow={{ "metal": true, "kapton": true, "proc": true }} extType={{ metal: "F0W", kapton: "F0W", tape: "FL0", proc: "F0W" }} bpSorted={bpSorted} />
      <SingleRow title="CuW HD Full" type="FHW" hrow={{ "metal": true, "kapton": true, "proc": true }} extType={{ metal: "F0W", kapton: "F0W", tape: "FH0", proc: "F0W" }} bpSorted={bpSorted} />
      <SingleRow title="Ti LD Full" type="FLT" mrow={{ "metal": true, "kapton": true, "proc": true }} extType={{ metal: "F0T", kapton: "F0T", tape: "FL0", proc: "F0T" }} bpSorted={bpSorted} gtape />
      <SingleRow title="Ti HD Full" type="FHT" hrow={{ "metal": true, "kapton": true, "proc": true }} extType={{ metal: "F0T", kapton: "F0T", tape: "FH0", proc: "F0T" }} bpSorted={bpSorted} gtape />
      {/* Unique to a single material*/}
      <SingleRow title="CuW HD Left" type="LHW" mrow={{ "metal": true }} extType={{ metal: "SHW" }} bpSorted={bpSorted} />
      <SingleRow title="CuW HD Right" type="RHW" hrow={{ "metal": true }} extType={{ metal: "SHW" }} bpSorted={bpSorted} />
      <SingleRow title="CuW HD Bottom" type="BHW" bpSorted={bpSorted} />
      <SingleRow title="Ti HD Top" type="THT" bpSorted={bpSorted} />
      {/*Common with tape*/}
      <SingleRow title="CuW LD Top" type="TLW" mrow={{ tape: true }} extType={{ tape: "TL0" }} bpSorted={bpSorted} />
      <SingleRow title="Ti LD Top" type="TLT" hrow={{ tape: true }} extType={{ tape: "TL0" }} bpSorted={bpSorted} />
      <SingleRow title="CuW LD Bottom" type="BLW" mrow={{ tape: true }} extType={{ tape: "BL0" }} bpSorted={bpSorted} />
      <SingleRow title="Ti LD Bottom" type="BLT" hrow={{ tape: true }} extType={{ tape: "BL0" }} bpSorted={bpSorted} />
      <SingleRow title="CuW LD Five" type="5LW" mrow={{ tape: true }} extType={{ tape: "5L0" }} bpSorted={bpSorted} />
      <SingleRow title="Ti LD Five" type="5LT" hrow={{ tape: true }} extType={{ tape: "5L0" }} bpSorted={bpSorted} />
      <SingleRow title="CuW LD Left" type="LLW" mrow={{ metal: true }} extType={{ metal: "SLW", tape: "LL0" }} bpSorted={bpSorted} />
      <SingleRow title="CuW LD Right" type="RLW" hrow={{ metal: true }} extType={{ metal: "SLW", tape: "RL0" }} bpSorted={bpSorted} />
      <SingleRow title="Ti LD Left" type="LLT" extType={{ tape: "LL0" }} bpSorted={bpSorted} gtape />
      <SingleRow title="Ti LD Right" type="RLT" extType={{ tape: "RL0" }} bpSorted={bpSorted} gtape />
    </tbody>
  </Table >
}

const SingleRow = ({ title, type, bpSorted, extType = {}, hrow = {}, mrow = {}, gtape = false }) => {
  const { kaptonList, tapeList, inboundList } = useContext(DatabaseContext)

  const mtype = extType["metal"] ? extType["metal"] : type;
  const ktype = extType["kapton"] ? extType["kapton"] : type;
  const ttype = extType["tape"] ? extType["tape"] : type;
  const ptype = extType["proc"] ? extType["proc"] : type;

  const tapeCount = GetVersionedCount(ttype, tapeList);
  const tapeStr = gtape ? `(${tapeCount})` : tapeCount
  const kapCount = GetVersionedCount(ktype, kaptonList);

  let inboundMetalCount = 0;
  let inboundKaptonCount = 0;
  let inboundTapeCount = 0;
  if ('id' in inboundList) {
    const sum = (acc, curr) => acc + curr
    const inb_metal = filterTable(inboundList, (inb) => (inb.type === "BAREPLATE") && (inb.geometry === mtype) && (inb.act_date === null))
    inboundMetalCount = inb_metal.quantity.reduce(sum, 0);
    const inb_kapton = filterTable(inboundList, (inb) => (inb.type === "KAPTON") && (inb.geometry === ktype) && (inb.act_date === null))
    inboundKaptonCount = inb_kapton.quantity.reduce(sum, 0);
    const inb_tape = filterTable(inboundList, (inb) => (inb.type === "TAPE") && (inb.geometry === ttype) && (inb.act_date === null))
    inboundTapeCount = inb_tape.quantity.reduce(sum, 0);
  }

  const MetalStatus = () => {
    const good = bpSorted[mtype]["bare_good"].length;
    const unknown = bpSorted[mtype]["bare_unknown"].length;
    return <>
      {good + unknown} <br />
      <span style={{ marginRight: "0.5em" }}><BPCountCell name={`${mtype}_bare_good`} bpList={bpSorted[mtype]["bare_good"]} /></span> |
      <span style={{ color: "gray", marginLeft: "0.5em", marginRight: "0.5em" }}>
        <BPCountCell name={`${mtype}_bare_unknown`} bpList={bpSorted[ptype]["bare_unknown"]} />{(inboundMetalCount > 0) && `(+${inboundMetalCount})`}
      </span> |
      <span style={{ color: "red", marginLeft: "0.5em" }}><BPCountCell name={`${mtype}_bare_bad`} bpList={bpSorted[ptype]["bare_bad"]} /></span>
    </>
  }

  const DefectStatus = () => {
    const delam = bpSorted[ptype]["delam"].length;
    const recov = bpSorted[ptype]["recov"].length;
    return <>
      <span style={{ color: "darkgray" }}>{delam + recov}</span><br />
      <span style={{ color: "red", marginRight: "0.5em" }}><BPCountCell name={`${type}_delam`} bpList={bpSorted[ptype]["delam"]} /></span> |
      <span style={{ color: "orange", marginLeft: "0.5em" }}><BPCountCell name={`${type}_recov`} bpList={bpSorted[ptype]["recov"]} /></span>
    </>
  }

  const PartialStatus = () => {
    const lam_in = bpSorted[ptype]["lam_in"].length;
    const lam_comp = bpSorted[ptype]["lam_comp"].length;
    const lam_remote = bpSorted[ptype]["lam_remote"].length;
    return <>
      {lam_in + lam_comp + lam_remote}<br />
      <span style={{ color: "blue", marginRight: "0.5em" }}><BPCountCell name={`${type}_lam_in`} bpList={bpSorted[ptype]["lam_in"]} /></span> |
      <span style={{ color: "darkgreen", marginLeft: "0.5em", marginRight: "0.5em" }}><BPCountCell name={`${type}_lam_comp`} bpList={bpSorted[ptype]["lam_comp"]} /></span> |
      <span style={{ color: "purple", marginLeft: "0.5em" }}><BPCountCell name={`${type}_lam_remote`} bpList={bpSorted[ptype]["lam_remote"]} /></span>
    </>
  }

  const ReadyStatus = () => {
    const comp = bpSorted[type]["comp"].length
    const pack = bpSorted[type]["pack"].length
    const comp_remote = bpSorted[type]["comp_remote"].length
    return <>
      {comp + pack + comp_remote} <br />
      <span style={{ color: "blue", marginRight: "0.5em" }}><BPCountCell name={`${type}_comp`} bpList={bpSorted[type]["comp"]} /></span> |
      <span style={{ color: "darkgreen", marginLeft: "0.5em", marginRight: "0.5em" }}><BPCountCell name={`${type}_pack`} bpList={bpSorted[type]["pack"]} /></span> |
      <span style={{ color: "purple", marginLeft: "0.5em" }}><BPCountCell name={`${type}_pack`} bpList={bpSorted[type]["comp_remote"]} /></span>
    </>
  }

  const ProjectStatus = () => {
    const total = projectTotal(type, true);
    const total_min = projectTotal(type, false);
    const ship = bpSorted[type]["ship"].length
    const ship_per = 100 * ship / total;
    const ready = bpSorted[type]["pack"].length + bpSorted[type]["comp"].length;
    const ready_remote = bpSorted[type]["comp_remote"].length;
    const ready_remote_per = 100 * ready_remote / total;
    const ready_per = 100 * ready / total;
    const miss = Math.max(total - ready - ship, 0);
    const miss_per = 100 * miss / total;

    const partial_remote = Math.min(bpSorted[ptype]["lam_remote"].length, tapeCount);
    const partial_remote_per = 100 * partial_remote / total;

    let running_partial = bpSorted[ptype]["lam_in"].length + bpSorted[ptype]["lam_comp"].length + bpSorted[ptype]["recov"].length;
    let running_bare = bpSorted[mtype]["bare_good"].length + bpSorted[mtype]["bare_unknown"].length;
    let running_tape = tapeCount - partial_remote;
    let running_kap = kapCount;
    // Subtracting the stuff that is already laminated and is good
    let running_comp_count = 0
    if (total - ship - ready - ready_remote > 0) { // Only calculating there is still stuff to calculate
      running_comp_count = Math.min(running_tape, running_partial, total - ship - ready - ready_remote)
      running_tape -= running_comp_count;
      running_partial -= running_comp_count;
      // Subtracting bare metal
      let running_assembly = Math.min(
        running_tape, running_kap, bpSorted[mtype]["bare_good"].length + bpSorted[mtype]["bare_unknown"].length
      )
      running_comp_count += running_assembly
      running_tape -= running_assembly;
      running_kap -= running_assembly;
      running_bare -= running_assembly;
    }
    // Calculating the percentage
    const has_comp_per = 100 * running_comp_count / total;

    // Adding inbound items
    running_tape += inboundTapeCount
    running_kap += inboundKaptonCount
    running_bare += inboundMetalCount
    let running_inbound_partial = Math.min(running_tape, running_partial)
    running_tape -= running_inbound_partial
    running_partial -= running_inbound_partial
    let running_inbound_assembly = Math.min(running_tape, running_kap, running_bare)
    // Only counting if existing components is not enough
    const running_inbound = (total - ship - ready - running_comp_count <= 0) ? 0 : Math.min(running_inbound_partial + running_inbound_assembly, total - ship - ready - running_comp_count);
    const running_inbound_per = running_inbound / total * 100

    return <>
      <span style={{ marginRight: "1em" }}>{total_min} ({total})</span>  |
      <span style={{ color: "darkgreen", marginLeft: "1em", marginRight: "1em" }}>{ship} ({ship_per.toFixed(1)}%)</span> |
      <span style={{ color: "blue", marginLeft: "1em", marginRight: "1em" }}>{ready}+{ready_remote} ({ready_per.toFixed(1)}+{ready_remote_per.toFixed(1)}%)</span> |
      <span style={{ color: "red", marginLeft: "1em", marginRight: "1em" }}>{miss} ({miss_per.toFixed(1)}%) </span> <br />
      <ProgressBar>
        <ProgressBar variant="success" now={ship_per} key={`${type}_shipped_progress`} label={ship} />
        <ProgressBar variant="success" now={ready_remote_per} striped key={`${type}_ready_remote_progress}`} label={ready_remote} />
        <ProgressBar variant="info" now={ready_per} key={`${type}_ready_progress}`} label={ready} />
        <ProgressBar variant="warning" now={partial_remote_per} striped key={`${type}_partial_progress}`} label={partial_remote} />
        <ProgressBar variant="warning" now={has_comp_per} key={`${type}_comp_progress}`} label={running_comp_count} />
        <ProgressBar style={{ backgroundColor: "gray" }} striped now={running_inbound_per} key={`${type}_inbound_progress`} label={running_inbound} />
      </ProgressBar>
    </>
  }

  return <tr style={{ verticalAlign: "middle", textAlign: "center" }}>
    <th>{title}</th>
    {!("metal" in hrow) && <td rowSpan={"metal" in mrow ? 2 : 1}><MetalStatus /></td>}
    {!("kapton" in hrow) && <td rowSpan={"kapton" in mrow ? 2 : 1}>{kapCount} {inboundKaptonCount > 0 && <span style={{ color: "gray" }}>(+{inboundKaptonCount})</span>}</td>}
    {!("tape" in hrow) && <td rowSpan={"tape" in mrow ? 2 : 1} style={{ color: gtape ? "gray" : 'black' }}>{tapeStr} {inboundTapeCount > 0 && <span style={{ color: "gray" }}>(+{inboundTapeCount})</span>}</td>}
    {!("proc" in hrow) && <td rowSpan={"proc" in mrow ? 2 : 1}><DefectStatus /></ td>}
    {!("proc" in hrow) && <td rowSpan={"proc" in mrow ? 2 : 1}><PartialStatus /></ td>}
    <td><ReadyStatus /></td>
    <td><ProjectStatus /></td>
  </tr >
}

const BPCountCell = ({ name, bpList }) => {
  if (bpList.length === 0 || !name) return <>{bpList.length}</>
  const idDisplay = bpList.map((bp) =>
  (bp.bar_code !== null ? bp.bar_code :
    bp.bare_barcode !== null ? bp.bare_barcode :
      bp.workshop_serial)
  ); // List of baseplates in a certain category

  const makeDownloadFile = () => {
    const blob = new Blob([idDisplay.join("\n")], { type: "text/plain" });
    const filename = `countList_${name}_${dateYYYYMMMDD()}.txt`;
    createBlobDownload(blob, filename);
  }

  return <>
    <OverlayTrigger overlay={
      <Tooltip id={bpList[0]}>{idDisplay.join(", ")}</Tooltip>
    }>
      {/* eslint-disable-next-line */}
      <a style={{ textDecoration: name ? "underline" : "none" }} onClick={makeDownloadFile}>{bpList.length}</a>
    </OverlayTrigger>
  </>
}

const Home = () => {
  const { baseplateList, kaptonList, tapeList, shipmentList } = useContext(DatabaseContext)
  const bpSorted = SortMetalForTable(baseplateList, kaptonList, tapeList, shipmentList);

  const shipTypes = Object.keys(bpSorted).filter((t) => (t[1] !== "0" && t[0] !== "S"))
  const total = shipTypes.map((t) => projectTotal(t, true)).reduce((acc, cur) => acc + cur, 0);
  const ship = shipTypes.map((t) => bpSorted[t]["ship"].length).reduce((acc, cur) => acc + cur, 0);
  const ready = shipTypes.map((t) => bpSorted[t]["comp"].length + bpSorted[t]["pack"].length).reduce((acc, cur) => acc + cur, 0);
  const miss = Math.max(total - ship - ready, 0)
  const ship_per = 100 * ship / total
  const read_per = 100 * ready / total

  return <div style={{ width: "100%", justifyContent: "center", alignItems: "center", display: "flex" }}>
    <div style={{ width: "1600px" }}>
      <h2>Project Progress</h2>
      <b> (shipped/<span style={{ color: "gray" }}>+ready</span>/total)</b>
      <span style={{ paddingLeft: "0.5em", paddingRight: "0.5em" }}>|</span>
      Count:  {ship}/<span style={{ color: "gray" }}>{ship + ready}</span>/{total}
      <span style={{ paddingLeft: "0.5em", paddingRight: "0.5em" }}>|</span>
      Percentage: {ship_per.toFixed(1)}%/<span style={{ color: "gray" }}>{(read_per + ship_per).toFixed(1)}%</span> / --
      <ProgressBar>
        <ProgressBar variant="success" now={100 * ship / total} key={`overview_shipped_progress`} label={`Shipped: ${ship} (${ship_per.toFixed(1)}%)`} />
        <ProgressBar variant="info" now={100 * ready / total} key={`overview_ready_progress`} label={`Ready: ${ready} (${read_per.toFixed(1)}%)`} />
        <ProgressBar style={{ backgroundColor: "gray" }} striped now={100 * miss / total} key={`overview_missing_progress`} label={`Missing: ${miss} (${(miss / total * 100).toFixed(1)}%)`} />
      </ProgressBar>
      <br />
      <h2>Component stock</h2>
      <StockTable bpSorted={bpSorted} />
    </div>
  </div>;
}


export default Home;
